import { useCallback } from 'react';

import { useAuth0 } from '@auth0/auth0-react';

import useGetClientId from '@/providers/UserPermissionsProvider/hooks/useGetClientId';

export type SubmitFeedbackArgs = {
  /** Thread id from the conversation to score. */
  threadId: string;
  /** Score name, e.g. 'thumbs'. */
  name: string;
  /** Score value; numeric or categorical. */
  value: string | number;
  /** Optional comment to attach to the score. */
  comment?: string;
};

export type UseAgentFeedbackArgs = {
  /** Agent id to associate with the feedback. */
  agentId: string;
};

export type UseAgentFeedbackResult = {
  /** POSTs the score. Rejects if the request fails. */
  submitFeedback: (args: SubmitFeedbackArgs) => Promise<void>;
};

const agentsBaseUrl = import.meta.env.VITE_INK_AGENTS_URI as string | undefined;

/**
 * Posts a Langfuse score to ink-agents feedback endpoint
 * (`POST /api/v1/feedback`). Mirrors the auth wiring of
 * `ConnectableHttpAgent` (Bearer token per request + `X-CLIENT-ID`).
 */
export const useAgentFeedback = ({
  agentId,
}: UseAgentFeedbackArgs): UseAgentFeedbackResult => {
  const { getAccessTokenSilently } = useAuth0();
  const clientId = useGetClientId();
  const buildHeaders = useCallback(async (): Promise<
    Record<string, string>
  > => {
    const token = await getAccessTokenSilently();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (clientId) headers['X-CLIENT-ID'] = clientId;
    return headers;
  }, [getAccessTokenSilently, clientId]);

  const submitFeedback = useCallback(
    async ({ threadId, name, value, comment }: SubmitFeedbackArgs) => {
      if (!agentsBaseUrl) {
        throw new Error('Missing VITE_INK_AGENTS_URI');
      }

      const headers = await buildHeaders();
      headers['Content-Type'] = 'application/json';

      const body: Record<string, unknown> = {
        agent: agentId,
        thread_id: threadId,
        name,
        value,
      };
      if (comment) {
        body.comment = comment;
      }

      const res = await fetch(`${agentsBaseUrl}/api/v1/feedback`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        throw new Error(`Failed to submit feedback (${res.status})`);
      }
    },
    [agentId, buildHeaders],
  );

  return {
    submitFeedback,
  };
};
